import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, CopyIcon, CheckIcon, MailIcon, LinkIcon, UsersIcon, ExternalLinkIcon, ShareIcon, SparklesIcon, CodeIcon, CheckCircle2Icon } from 'lucide-react';
import { Account } from '../types';
import { toast } from 'sonner';
interface ShareDrawerProps {
  account: Account;
  onClose: () => void;
}
export function ShareDrawer({
  account,
  onClose
}: ShareDrawerProps) {
  const [copied, setCopied] = useState<string | null>(null);
  const [selectedBot, setSelectedBot] = useState<'external' | 'internal'>('external');
  const externalBot = account.bots.find(b => b.type === 'external');
  const internalBot = account.bots.find(b => b.type === 'internal');
  const selectedBotData = selectedBot === 'external' ? externalBot : internalBot;
  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    toast.success(`${label} copied to clipboard`);
    setTimeout(() => setCopied(null), 2000);
  };
  const shareUrl = `${window.location.origin}?accountId=${account.id}&bot=${selectedBot}`;
  const embedCode = `<iframe src="${shareUrl}" width="100%" height="600px" frameborder="0"></iframe>`;
  return <>
      {/* Backdrop */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" data-id="element-1296" />
      {/* Drawer */}
      <motion.div initial={{
      x: '100%'
    }} animate={{
      x: 0
    }} exit={{
      x: '100%'
    }} transition={{
      type: 'spring',
      damping: 30,
      stiffness: 300
    }} className="fixed right-0 top-0 bottom-0 w-full md:w-[520px] bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 border-l border-slate-800/80 z-50 flex flex-col shadow-2xl overflow-hidden" data-id="element-1297">
        {/* Decorative gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 pointer-events-none" data-id="element-1298" />
        {/* Header */}
        <div className="relative flex-shrink-0 px-6 py-5 border-b border-slate-800/80 bg-slate-950/80 backdrop-blur-xl" data-id="element-1299">
          <div className="flex items-center justify-between" data-id="element-1300">
            <div className="flex items-center gap-3" data-id="element-1301">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center shadow-lg shadow-blue-600/30" data-id="element-1302">
                <ShareIcon className="w-5 h-5 text-white" data-id="element-1303" />
              </div>
              <div data-id="element-1304">
                <h3 className="text-lg font-bold text-white" data-id="element-1305">Share Access</h3>
                <p className="text-xs text-slate-400 font-medium" data-id="element-1306">
                  {account.name}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 text-slate-400 hover:text-white transition-all rounded-lg hover:bg-slate-800/60" data-id="element-1307">
              <XIcon className="w-5 h-5" data-id="element-1308" />
            </button>
          </div>
        </div>
        {/* Content - Scrollable */}
        <div className="relative flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent" data-id="element-1309">
          <div className="p-6 space-y-6" data-id="element-1310">
            {/* Bot Selection - Compact */}
            <div data-id="element-1311">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 block" data-id="element-1312">
                Select Chatbot
              </label>
              <div className="grid grid-cols-2 gap-2" data-id="element-1313">
                {externalBot && <button onClick={() => setSelectedBot('external')} className={`relative p-3 rounded-lg border transition-all text-left overflow-hidden ${selectedBot === 'external' ? 'bg-blue-600/10 border-blue-500/50 ring-1 ring-blue-500/30' : 'bg-slate-800/40 border-slate-700/50 hover:border-slate-600'}`} data-id="element-1314">
                    <div className="flex items-center gap-2 mb-1" data-id="element-1315">
                      <ExternalLinkIcon className={`w-3.5 h-3.5 ${selectedBot === 'external' ? 'text-blue-400' : 'text-slate-500'}`} data-id="element-1316" />
                      <span className={`text-sm font-semibold ${selectedBot === 'external' ? 'text-white' : 'text-slate-300'}`} data-id="element-1317">
                        {externalBot.name}
                      </span>
                    </div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider" data-id="element-1318">
                      External Bot
                    </div>
                    {selectedBot === 'external' && <CheckCircle2Icon className="absolute top-2 right-2 w-4 h-4 text-blue-400" data-id="element-1319" />}
                  </button>}
                {internalBot && <button onClick={() => setSelectedBot('internal')} className={`relative p-3 rounded-lg border transition-all text-left overflow-hidden ${selectedBot === 'internal' ? 'bg-purple-600/10 border-purple-500/50 ring-1 ring-purple-500/30' : 'bg-slate-800/40 border-slate-700/50 hover:border-slate-600'}`} data-id="element-1320">
                    <div className="flex items-center gap-2 mb-1" data-id="element-1321">
                      <UsersIcon className={`w-3.5 h-3.5 ${selectedBot === 'internal' ? 'text-purple-400' : 'text-slate-500'}`} data-id="element-1322" />
                      <span className={`text-sm font-semibold ${selectedBot === 'internal' ? 'text-white' : 'text-slate-300'}`} data-id="element-1323">
                        {internalBot.name}
                      </span>
                    </div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider" data-id="element-1324">
                      Internal Bot
                    </div>
                    {selectedBot === 'internal' && <CheckCircle2Icon className="absolute top-2 right-2 w-4 h-4 text-purple-400" data-id="element-1325" />}
                  </button>}
              </div>
            </div>
            {/* Direct Link */}
            <div className="bg-slate-900/60 border border-slate-800/60 rounded-xl p-4 space-y-3" data-id="element-1326">
              <div className="flex items-center justify-between" data-id="element-1327">
                <div className="flex items-center gap-2" data-id="element-1328">
                  <LinkIcon className="w-4 h-4 text-blue-400" data-id="element-1329" />
                  <h4 className="text-sm font-bold text-white" data-id="element-1330">Direct Link</h4>
                </div>
                <button onClick={() => handleCopy(shareUrl, 'Link')} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all flex items-center gap-1.5 text-xs font-semibold shadow-lg shadow-blue-600/20" data-id="element-1331">
                  <AnimatePresence mode="wait" data-id="element-1332">
                    {copied === 'Link' ? <motion.div key="check" initial={{
                    scale: 0
                  }} animate={{
                    scale: 1
                  }} exit={{
                    scale: 0
                  }} data-id="element-1333">
                        <CheckIcon className="w-3.5 h-3.5" data-id="element-1334" />
                      </motion.div> : <motion.div key="copy" initial={{
                    scale: 0
                  }} animate={{
                    scale: 1
                  }} exit={{
                    scale: 0
                  }} data-id="element-1335">
                        <CopyIcon className="w-3.5 h-3.5" data-id="element-1336" />
                      </motion.div>}
                  </AnimatePresence>
                  {copied === 'Link' ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed" data-id="element-1337">
                Share this link to give direct access to the chatbot
              </p>
              <div className="bg-slate-950/60 border border-slate-700/50 rounded-lg p-3" data-id="element-1338">
                <code className="text-xs text-blue-400 font-mono break-all block" data-id="element-1339">
                  {shareUrl}
                </code>
              </div>
            </div>
            {/* Embed Code */}
            <div className="bg-slate-900/60 border border-slate-800/60 rounded-xl p-4 space-y-3" data-id="element-1340">
              <div className="flex items-center justify-between" data-id="element-1341">
                <div className="flex items-center gap-2" data-id="element-1342">
                  <CodeIcon className="w-4 h-4 text-purple-400" data-id="element-1343" />
                  <h4 className="text-sm font-bold text-white" data-id="element-1344">Embed Code</h4>
                </div>
                <button onClick={() => handleCopy(embedCode, 'Embed code')} className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-all flex items-center gap-1.5 text-xs font-semibold shadow-lg shadow-purple-600/20" data-id="element-1345">
                  <AnimatePresence mode="wait" data-id="element-1346">
                    {copied === 'Embed code' ? <motion.div key="check" initial={{
                    scale: 0
                  }} animate={{
                    scale: 1
                  }} exit={{
                    scale: 0
                  }} data-id="element-1347">
                        <CheckIcon className="w-3.5 h-3.5" data-id="element-1348" />
                      </motion.div> : <motion.div key="copy" initial={{
                    scale: 0
                  }} animate={{
                    scale: 1
                  }} exit={{
                    scale: 0
                  }} data-id="element-1349">
                        <CopyIcon className="w-3.5 h-3.5" data-id="element-1350" />
                      </motion.div>}
                  </AnimatePresence>
                  {copied === 'Embed code' ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed" data-id="element-1351">
                Embed the chatbot directly into your website
              </p>
              <div className="bg-slate-950/60 border border-slate-700/50 rounded-lg p-3" data-id="element-1352">
                <code className="text-xs text-purple-400 font-mono break-all block whitespace-pre-wrap" data-id="element-1353">
                  {embedCode}
                </code>
              </div>
            </div>
            {/* Email Share */}
            <div className="bg-slate-900/60 border border-slate-800/60 rounded-xl p-4 space-y-3" data-id="element-1354">
              <div className="flex items-center gap-2" data-id="element-1355">
                <MailIcon className="w-4 h-4 text-green-400" data-id="element-1356" />
                <h4 className="text-sm font-bold text-white" data-id="element-1357">
                  Share via Email
                </h4>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed" data-id="element-1358">
                Send an email invitation with access details
              </p>
              <button onClick={() => {
              const subject = encodeURIComponent(`Access to ${account.name} - ${selectedBotData?.name}`);
              const body = encodeURIComponent(`You've been granted access to ${account.name}'s ${selectedBotData?.name} chatbot.\n\nAccess the chatbot here:\n${shareUrl}\n\nPurpose: ${selectedBotData?.purpose}`);
              window.location.href = `mailto:?subject=${subject}&body=${body}`;
            }} className="w-full px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-all flex items-center justify-center gap-2 shadow-lg shadow-green-600/20 font-semibold text-sm" data-id="element-1359">
                <MailIcon className="w-4 h-4" data-id="element-1360" />
                Open Email Client
              </button>
            </div>
            {/* Access Details */}
            <div className="bg-slate-900/60 border border-slate-800/60 rounded-xl p-4" data-id="element-1361">
              <div className="flex items-center gap-2 mb-4" data-id="element-1362">
                <SparklesIcon className="w-4 h-4 text-amber-400" data-id="element-1363" />
                <h4 className="text-sm font-bold text-white" data-id="element-1364">Access Details</h4>
              </div>
              <div className="space-y-2" data-id="element-1365">
                <div className="flex items-center justify-between py-2 border-b border-slate-800/60" data-id="element-1366">
                  <span className="text-xs text-slate-400" data-id="element-1367">Bot Type</span>
                  <span className="text-xs font-semibold text-white" data-id="element-1368">
                    {selectedBot === 'external' ? 'External' : 'Internal'}
                  </span>
                </div>
                <div className="flex items-start justify-between py-2 border-b border-slate-800/60" data-id="element-1369">
                  <span className="text-xs text-slate-400" data-id="element-1370">Purpose</span>
                  <span className="text-xs font-medium text-white text-right max-w-[60%]" data-id="element-1371">
                    {selectedBotData?.purpose}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2" data-id="element-1372">
                  <span className="text-xs text-slate-400" data-id="element-1373">
                    Knowledge Spaces
                  </span>
                  <span className="text-xs font-semibold text-white" data-id="element-1374">
                    {selectedBotData?.bound_spaces.length || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Footer */}
        <div className="relative flex-shrink-0 px-6 py-4 border-t border-slate-800/80 bg-slate-950/80 backdrop-blur-xl" data-id="element-1375">
          <div className="flex items-center justify-between" data-id="element-1376">
            <p className="text-xs text-slate-500 font-medium" data-id="element-1377">
              Shared links expire after 30 days
            </p>
            <button onClick={onClose} className="px-4 py-2 bg-slate-800/60 hover:bg-slate-700/60 border border-slate-700/50 hover:border-slate-600 text-white rounded-lg transition-all font-semibold text-sm" data-id="element-1378">
              Done
            </button>
          </div>
        </div>
      </motion.div>
    </>;
}